/* global URL_AGENT_CHAT */
import { Dispatch, FormEvent, RefObject, SetStateAction, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@context/AuthContext';
import { ga4Event } from '@utils/analytics/ga4';
import { resolveAgentRoute } from './agentRouteMap';

/**
 * usePaulinaChat — toda la lógica del chat con Paulina (asistente de soporte/CS).
 *
 * Extraído 1:1 de AgentChatWidget para reutilizarlo desde distintas superficies
 * (pill desktop, bottom-sheet móvil) sin duplicar comportamiento. Habla con el
 * Worker de Cloudflare (plick-agent-pc): POST /api/chat para enviar y GET
 * /api/chat/:id por polling hasta obtener la respuesta. El Worker valida la firma
 * del JWT de sesión del panel (el mismo token de localStorage que usan las APIs) —
 * la identidad nunca viaja en el body.
 *
 * Es un refactor PURO: el hook expone el estado y las acciones que antes vivían
 * inline en el componente, con el mismo aislamiento multi-tenant (storageKey por
 * comercio+usuario + effect de sesionId), la misma telemetría pau_* y el mismo
 * contrato ACCION_IR por llave (resolveAgentRoute).
 */

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface Nudge {
  id: string;
  tipo: string;
  titulo: string;
  mensaje: string;
  cta:
    | { tipo: 'navegar'; path: string; seccion: string; label: string }
    | { tipo: 'escalar'; link: string; label: string }
    | { tipo: 'chat'; prompt: string; label: string };
}

// Convención de naming de eventos: `pau_<dominio>_<accion>` — prefijo pna
// (Paulina, sin doble sentido) para agruparlos alfabéticamente y encontrarlos en el search de
// Clarity/GA4; dominio + acción explícitos en pasado. Catálogo:
//   pau_chat_abierto · pau_chat_limpiado
//   pau_mensaje_enviado · pau_respuesta_ok · pau_respuesta_error
//   pau_navegacion_click
// track() los manda a GA4 (con params) y a Clarity (tag de sesión pau_usuario
// + evento sin params). Sin PII.
const track = (eventName: string, params: Record<string, string | number | boolean> = {}) => {
  try {
    ga4Event(eventName, params);
    const w = window as unknown as { clarity?: (...a: unknown[]) => void };
    if (typeof w.clarity === 'function') {
      w.clarity('set', 'pau_usuario', 'true');
      w.clarity('event', eventName);
    }
  } catch {
    /* la telemetría nunca rompe el chat */
  }
};

const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 90000;
const MAX_HISTORY = 20;
// Aislamiento multi-tenant: la copia de DISPLAY del historial se guarda por
// (comercio, usuario), nunca en una clave global — así un login distinto en la
// misma pestaña jamás lee la conversación del negocio anterior. La verdad de la
// conversación vive server-side; esto es solo el caché de pantalla.
const storageKey = (): string => {
  const c = localStorage.getItem('id_commerce') || 'anon';
  const u = localStorage.getItem('id_user') || 'anon';
  return `agent_chat_history:${c}:${u}`;
};

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const loadHistory = (): ChatTurn[] => {
  try {
    const raw = sessionStorage.getItem(storageKey());
    return raw ? (JSON.parse(raw) as ChatTurn[]) : [];
  } catch {
    return [];
  }
};

const saveHistory = (turns: ChatTurn[]) => {
  try {
    sessionStorage.setItem(storageKey(), JSON.stringify(turns.slice(-MAX_HISTORY)));
  } catch {
    /* storage lleno o bloqueado: el chat sigue funcionando sin persistencia */
  }
};

// Cadencia: un nudge del mismo tipo no se repite en X horas (localStorage).
const NUDGE_COOLDOWN_H: Record<string, number> = {
  bienvenida: 9999,
  setup_incompleto: 24,
  diagnostico: 6,
  descubrimiento: 48,
  rageclick: 4,
};
const nudgeEnCooldown = (tipo: string): boolean => {
  try {
    const last = Number(localStorage.getItem(`pau_nudge_${tipo}`) || 0);
    const h = NUDGE_COOLDOWN_H[tipo] ?? 24;
    return Date.now() - last < h * 3600 * 1000;
  } catch {
    return false;
  }
};
const marcarNudge = (tipo: string) => {
  try {
    localStorage.setItem(`pau_nudge_${tipo}`, String(Date.now()));
  } catch {
    /* sin storage: se mostrará otra vez, aceptable */
  }
};

export interface PaulinaChat {
  /** ¿El agente debe renderizarse? (auth + rol + URL configurada). */
  enabled: boolean;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  turns: ChatTurn[];
  input: string;
  setInput: Dispatch<SetStateAction<string>>;
  busy: boolean;
  error: string;
  nudge: Nudge | null;
  bottomRef: RefObject<HTMLDivElement>;
  /** Abre/cierra el chat instrumentando pau_chat_abierto al abrir. */
  toggleOpen: () => void;
  /** Navega a una sección por llave (contrato ACCION_IR → resolveAgentRoute). */
  goTo: (seccion: string) => void;
  aceptarNudge: (n: Nudge) => void;
  descartarNudge: (n: Nudge) => void;
  clearConversation: () => void;
  /** Submit del formulario de input (envía input.trim()). */
  send: (e: FormEvent) => void;
  /** Envío directo de un mensaje (usado por nudges de tipo chat). */
  doSend: (message: string) => Promise<void>;
}

export const usePaulinaChat = (): PaulinaChat => {
  const { isAuthenticated, isDeliveryPerson, user, commerceName } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<ChatTurn[]>(loadHistory);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [nudge, setNudge] = useState<Nudge | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const enabled = !(!isAuthenticated || isDeliveryPerson || typeof URL_AGENT_CHAT === 'undefined' || !URL_AGENT_CHAT);

  // Aislamiento entre sesiones/sucursales: al cambiar de identidad (logout→login O
  // cambio de sucursal vía refreshLogin — ambos SPA sin reload), recargar el historial
  // namespaceado de la sesión ACTUAL y cerrar el chat, para no arrastrar la conversación
  // de otro negocio/sucursal en pantalla ni enviarla al backend.
  const sesionId = `${localStorage.getItem('id_commerce') ?? ''}:${localStorage.getItem('id_user') ?? ''}`;
  useEffect(() => {
    setTurns(loadHistory());
    setOpen(false);
    setNudge(null);
  }, [sesionId]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [open, turns, busy]);

  // Rol de activación: pide nudges proactivos al Worker (1 vez por sesión).
  useEffect(() => {
    if (!isAuthenticated || isDeliveryPerson) return;
    if (typeof URL_AGENT_CHAT === 'undefined' || !URL_AGENT_CHAT) return;
    if (sessionStorage.getItem('pau_hints_done')) return;
    sessionStorage.setItem('pau_hints_done', '1');
    const token = localStorage.getItem('token');
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${URL_AGENT_CHAT}/api/hints`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) return;
        const { nudges } = (await res.json()) as { nudges: Nudge[] };
        const elegible = (nudges || []).find((n) => !nudgeEnCooldown(n.tipo));
        if (elegible) {
          setNudge(elegible);
          track('pau_proactivo_mostrado', { tipo: elegible.tipo, id: elegible.id });
        }
      } catch {
        /* sin nudges: experiencia normal */
      }
    })();
  }, [isAuthenticated, isDeliveryPerson]);

  // Rage click: 4+ clics rápidos en el mismo punto → ofrecer ayuda.
  useEffect(() => {
    let count = 0;
    let last = 0;
    let lx = 0;
    let ly = 0;
    const onClick = (e: MouseEvent) => {
      const now = Date.now();
      const cerca = Math.abs(e.clientX - lx) < 40 && Math.abs(e.clientY - ly) < 40;
      count = now - last < 600 && cerca ? count + 1 : 1;
      last = now;
      lx = e.clientX;
      ly = e.clientY;
      if (count >= 4 && !nudgeEnCooldown('rageclick')) {
        count = 0;
        track('pau_rage_click', { page: window.location.pathname });
        setNudge({
          id: 'rageclick',
          tipo: 'rageclick',
          titulo: '¿Te atoraste? 🤔',
          mensaje: 'Vi que le estás dando varias veces sin que pase nada. ¿Te echo la mano con lo que intentas hacer?',
          cta: {
            tipo: 'chat',
            prompt: 'Estoy batallando con algo en esta pantalla, ¿me ayudas?',
            label: 'Sí, ayúdame →',
          },
        });
      }
    };
    window.addEventListener('click', onClick);
    return () => window.removeEventListener('click', onClick);
  }, []);

  const doSend = async (message: string) => {
    if (!message || busy) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    const history = turns.slice(-MAX_HISTORY);
    const nextTurns: ChatTurn[] = [...turns, { role: 'user', content: message }];
    setTurns(nextTurns);
    setInput('');
    setBusy(true);
    setError('');
    track('pau_mensaje_enviado', { page: location.pathname, chars: message.length });
    const startedAt = Date.now();

    try {
      const res = await fetch(`${URL_AGENT_CHAT}/api/chat`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          history,
          // Defensa: user.name puede no venir (campo opcional del login);
          // fallback al nombre del comercio para no saludar con un nombre vacío.
          userName: user?.name || commerceName || undefined,
          // Contexto situacional: Paulina sabe en qué sección está el usuario.
          page: location.pathname,
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { chatId } = (await res.json()) as { chatId: string };

      const deadline = Date.now() + POLL_TIMEOUT_MS;
      let reply = '';
      // tools: nombres de las tools usadas por el Worker en este turno (contrato compartido del
      // polling al completar). Se emiten a Clarity/GA4 abajo; vacío → no se emite nada.
      let tools: string[] = [];
      while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        const poll = await fetch(`${URL_AGENT_CHAT}/api/chat/${chatId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!poll.ok) throw new Error(`HTTP ${poll.status}`);
        const data = (await poll.json()) as { status: string; reply?: string; tools?: string[] };
        if (data.status === 'complete') {
          reply = data.reply ?? '';
          tools = data.tools ?? [];
          break;
        }
        if (data.status === 'error') throw new Error('workflow error');
      }
      if (!reply) throw new Error('timeout');

      const finalTurns: ChatTurn[] = [...nextTurns, { role: 'assistant', content: reply }];
      setTurns(finalTurns);
      saveHistory(finalTurns);
      track('pau_respuesta_ok', {
        ms: Date.now() - startedAt,
        con_captura: /Captura:\s*http/.test(reply),
        con_navegacion: /ACCION_IR:/.test(reply),
      });
      // Una métrica por cada tool usada en el turno (GA4 + Clarity vía track). Si el turno no
      // usó tools, `tools` viene vacío y no se emite nada.
      for (const tool of tools) {
        track('pau_tool', { tool });
      }
    } catch (err) {
      setError('No pude obtener respuesta. Intenta de nuevo en un momento.');
      setTurns(turns);
      track('pau_respuesta_error', { motivo: err instanceof Error ? err.message.slice(0, 40) : 'desconocido' });
    } finally {
      setBusy(false);
    }
  };

  const goTo = (seccion: string) => {
    const path = resolveAgentRoute(seccion);
    if (!path) return;
    track('pau_navegacion_click', { seccion, path });
    setOpen(false);
    navigate(path);
  };

  const aceptarNudge = (n: Nudge) => {
    track('pau_proactivo_aceptado', { tipo: n.tipo, id: n.id });
    marcarNudge(n.tipo);
    setNudge(null);
    if (n.cta.tipo === 'navegar') navigate(n.cta.path);
    else if (n.cta.tipo === 'escalar') window.open(n.cta.link, '_blank');
    else {
      setOpen(true);
      doSend(n.cta.prompt);
    }
  };
  const descartarNudge = (n: Nudge) => {
    track('pau_proactivo_descartado', { tipo: n.tipo, id: n.id });
    marcarNudge(n.tipo);
    setNudge(null);
  };

  const clearConversation = () => {
    track('pau_chat_limpiado');
    try {
      sessionStorage.removeItem(storageKey());
    } catch {
      /* sin storage no hay nada que borrar */
    }
    setTurns([]);
    setError('');
  };

  const toggleOpen = () => {
    if (!open) track('pau_chat_abierto', { page: location.pathname });
    setOpen((v) => !v);
  };

  const send = (e: FormEvent) => {
    e.preventDefault();
    doSend(input.trim());
  };

  return {
    enabled,
    open,
    setOpen,
    turns,
    input,
    setInput,
    busy,
    error,
    nudge,
    bottomRef,
    toggleOpen,
    goTo,
    aceptarNudge,
    descartarNudge,
    clearConversation,
    send,
    doSend,
  };
};
